import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, PUBLIC_HOST } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  TrophyIcon,
  CoinsIcon,
  CalendarBlankIcon,
  CaretRightIcon,
  SealCheckIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TIER_CLASSES = {
  gold: "bg-gradient-to-br from-amber-300 to-amber-500 text-amber-950",
  silver: "bg-gradient-to-br from-zinc-300 to-zinc-400 text-zinc-900",
  bronze: "bg-gradient-to-br from-orange-300 to-orange-500 text-orange-950",
  purple: "bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white",
  emerald: "bg-gradient-to-br from-emerald-400 to-emerald-600 text-white",
};

function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtJoined(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function CollectorProfile() {
  const { user_id } = useParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .get(`/collectors/${user_id}`)
      .then((r) => alive && setProfile(r.data))
      .catch((e) => alive && setError(apiError(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [user_id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-24 text-center text-zinc-500 text-sm">
          Loading collector…
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-24 text-center" data-testid="collector-error">
          <p className="text-zinc-500 text-sm">{error}</p>
          <Link to="/" className="text-[#1D78AB] text-sm font-semibold mt-3 inline-block">
            ← Back home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { username, display_name, bio, avatar_url, cover_url, joined_at, stats, badges, recent_wins } = profile;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="collector-profile">
      <Navbar />

      {/* Hero header with cover + avatar */}
      <div
        className="relative h-56 sm:h-72 bg-gradient-to-br from-[#1D78AB] via-[#165D85] to-[#0E3F5C] overflow-hidden"
        data-testid="collector-hero"
      >
        {cover_url && (
          <img
            src={cover_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-16 sm:-mt-20 pb-16">
        {/* Avatar + name + meta */}
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 sm:gap-6">
          <div className="h-32 w-32 sm:h-40 sm:w-40 rounded-full border-4 border-white bg-zinc-200 overflow-hidden shadow-xl">
            {avatar_url ? (
              <img src={avatar_url} alt={display_name} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full grid place-items-center text-5xl font-display font-black text-zinc-400 bg-gradient-to-br from-zinc-100 to-zinc-200">
                {(display_name || username || "B").trim()[0].toUpperCase()}
              </div>
            )}
          </div>

          <div className="flex-1 pb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-zinc-900" data-testid="collector-name">
                {display_name || username || "Collector"}
              </h1>
              {badges.some((b) => b.key === "psa_10") && (
                <SealCheckIcon size={22} weight="fill" className="text-emerald-500" />
              )}
            </div>
            {username && username !== display_name && (
              <p className="text-sm text-zinc-500 font-mono">@{username}</p>
            )}
            {bio && <p className="mt-2 text-sm text-zinc-700 max-w-2xl">{bio}</p>}
            <div className="mt-2 flex items-center gap-1 text-xs text-zinc-500 font-mono tracking-widest uppercase">
              <CalendarBlankIcon size={12} weight="fill" /> Joined {fmtJoined(joined_at)}
            </div>
          </div>

          <ShareButton user_id={user_id} name={display_name || username} />
        </div>

        {/* Stat row */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="collector-stats">
          <StatCard
            icon={TrophyIcon}
            value={stats.win_count.toLocaleString()}
            label="Auction wins"
            accent="text-[#1D78AB]"
          />
          <StatCard
            icon={CoinsIcon}
            value={fmtMoney(stats.lifetime_spend)}
            label="Lifetime spend"
            accent="text-emerald-600"
          />
          <StatCard
            icon={SparkleIcon}
            value={badges.length}
            label="Badges earned"
            accent="text-amber-600"
          />
          <StatCard
            icon={SealCheckIcon}
            value={stats.psa_10_count || 0}
            label="PSA cards owned"
            accent="text-purple-600"
          />
        </div>

        {/* Badges */}
        <section className="mt-10" data-testid="collector-badges">
          <h2 className="text-sm font-mono tracking-widest uppercase text-zinc-500 mb-3">
            ━━ Badges
          </h2>
          {badges.length === 0 ? (
            <div className="rounded-2xl bg-white border-2 border-dashed border-zinc-200 p-8 text-center">
              <p className="text-sm text-zinc-500">
                No badges yet. Win your first auction to earn the <b>First Win</b> badge.
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {badges.map((b) => (
                <div
                  key={b.key}
                  className={`rounded-2xl px-4 py-3 shadow-sm ${TIER_CLASSES[b.tier] || "bg-zinc-800 text-white"}`}
                  data-testid={`badge-${b.key}`}
                  title={b.desc}
                >
                  <p className="font-display font-black text-sm tracking-tight">{b.label}</p>
                  <p className="text-[10px] font-mono tracking-wider uppercase opacity-80">
                    {b.desc}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recent wins */}
        <section className="mt-10" data-testid="collector-wins">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-mono tracking-widest uppercase text-zinc-500">
              ━━ Recent wins
            </h2>
            {recent_wins.length >= 12 && (
              <span className="text-[10px] font-mono tracking-widest uppercase text-zinc-400">
                Showing latest 12
              </span>
            )}
          </div>
          {recent_wins.length === 0 ? (
            <div className="rounded-2xl bg-white border-2 border-dashed border-zinc-200 p-8 text-center">
              <p className="text-sm text-zinc-500">No wins to show yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {recent_wins.map((w, i) => (
                <Link
                  key={`${w.listing_id}-${i}`}
                  to={`/listing/${w.listing_id}`}
                  className="rounded-2xl overflow-hidden bg-white border border-zinc-200 hover:border-[#1D78AB] transition-colors group"
                  data-testid={`win-${i}`}
                >
                  <div className="aspect-square bg-zinc-100">
                    {w.image ? (
                      <img
                        src={w.image}
                        alt={w.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : null}
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-semibold text-zinc-900 truncate">{w.title}</p>
                    <p className="text-[11px] text-emerald-600 font-mono font-bold">
                      {fmtMoney(w.amount)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      <Footer />
    </div>
  );
}

function StatCard({ icon: Icon, value, label, accent }) {
  return (
    <div className="rounded-2xl bg-white border border-zinc-200 p-4">
      <div className="flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase text-zinc-500">
        <Icon size={12} weight="fill" /> {label}
      </div>
      <div className={`font-display font-black text-2xl sm:text-3xl mt-1 tracking-tight ${accent}`}>
        {value}
      </div>
    </div>
  );
}

function ShareButton({ user_id, name }) {
  const share = async () => {
    const url = `${PUBLIC_HOST}/collector/${user_id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${name || "Collector"} on Bidorah`,
          url,
        });
        return;
      } catch {}
    }
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      toast.success("Profile link copied");
    }
  };
  return (
    <button
      onClick={share}
      className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-semibold self-start sm:self-end"
      data-testid="collector-share"
    >
      Share profile <CaretRightIcon size={14} weight="bold" />
    </button>
  );
}
